import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import RequestControllerRedux from "../../contexts/RequestController.js";
import TabelaRedux from "../../contexts/Tabela.js";
import Separator from "../../core/components/Separator.tsx";
import InputField from "../../core/data-entry/InputField.tsx";
import SelectInputField from "../../core/data-entry/SelectInputField.tsx";
import Modal from "../../core/feedback/Modal.tsx";
import { formatDateHora } from "../../core/utils/formatDate.ts";
import useService from "../../hooks/useService.ts";
import { Status } from "../../models/Status.ts";
import { lancarCteStatus } from "../../services/ModuloGerenciador/Embarque.js";
import CustomButtonV2 from "../CustomButtonV2/CustomButtonV2.js";
import LoadingComponent from "../LoadingComponent/LoadingComponent.tsx";
import ModalCadastro from "../ModalCadastro/ModalCadastro.tsx";
import "../ModalLancarCTEStatus/ModalLancarCTEStatus.css";
import NewIcon from "../NewIcon/NewIcon.js";

export interface SimpleDialogProps {
	titulo: string;
	tituloBotao: string;
	tituloConfirmar: string;
	texto: string;
	dataAlteracao: string;
	open: boolean;
	listaStatus: any[];
	setStatus: (e) => void;
	onClose: () => void;
	onClick: () => void;
	onSuccess: () => void;
	onError: (error) => void;
}

function ModalLancarCTEStatus(props: SimpleDialogProps) {
	const { setDadosRequestControllerRedux } = useContext(RequestControllerRedux);
	const { profile } = useProfile();
	const { tabelaRedux } = useContext(TabelaRedux);

	const { onClose, open, onSuccess, onError } = props;

	const [valuesSelect, setValuesSelect] = useState({
		status: {
			title: "",
			obj: "",
		},
	});

	const [cte, setCTE] = useState<string>("");

	const [dataHoraEmbarcado, setDataHoraEmbarcado] = useState<string>("");

	const [status, setStatus] = useState<Status | null>(null);

	const [statusOptions, setStatusOptions] = useState(
		props.listaStatus.map((item) => ({
			title: item.nome,
			obj: item,
		}))
	);

	const [errorStatus, setErrorStatus] = useState<boolean>(false);

	const [errorCte, setErrorCte] = useState<boolean>(false);

	const [errorDataHoraEmbarcado, setErrorDataHoraEmbarcado] =
		useState<boolean>(false);

	const [idAtual, setIdAtual] = useState(0);

	const [disable, setDisable] = useState(false);

	const [openCadastroStatus, setOpenCadastroStatus] = useState(false);

	const [lancandoCTE, lancarCTE] = useService(
		() =>
			lancarCteStatus({
				cte,
				dataHoraEmbarcado,
				token: profile?.token,
				EmbarqueId: tabelaRedux?.row?.idEmbarque,
				statusId: status?.idStatus || null,
			}),
		{
			autoStart: false,
			onData: () => {
				onSuccess();
				handleClose();
			},
			onError: (error) => {
				onError(error);
			},
		}
	);

	const handleStatusInputChange = useCallback(
		(inputValue) => {
			const normalizedInput = inputValue.target.value.trim().toLowerCase();

			const filteredOptions = props.listaStatus
				.filter((option) => option.nome.toLowerCase().includes(normalizedInput))
				.map((item) => ({
					title: item.nome,
					obj: item,
				}));

			setStatusOptions(filteredOptions);
		},
		[props.listaStatus]
	);

	const handleClose = useCallback(() => {
		setCTE("");
		setDataHoraEmbarcado("");
		setStatus(null);
		setErrorStatus(false);
		setErrorCte(false);
		setErrorDataHoraEmbarcado(false);
		onClose();
	}, [onClose]);

	const handleDefinirCteStatus = useCallback(() => {
		if (!status) {
			setErrorStatus(true);
			return;
		}
		if (!cte) {
			setErrorCte(true);
			return;
		}
		if (!dataHoraEmbarcado) {
			setErrorDataHoraEmbarcado(true);
			return;
		}
		if (!tabelaRedux?.row?.idEmbarque) {
			onError("Embarque não encontrado.");
			return;
		}
		setErrorCte(false);
		setErrorDataHoraEmbarcado(false);
		setErrorStatus(false);
		lancarCTE();
	}, [cte, dataHoraEmbarcado, lancarCTE, status]);

	const formattedDate = useMemo(() => {
		return tabelaRedux?.row?.dataHoraEmbarcado
			? formatDateHora(new Date(tabelaRedux?.row?.dataHoraEmbarcado))
			: "";
	}, [tabelaRedux?.row?.dataHoraEmbarcado]);

	useEffect(() => {
		setCTE(tabelaRedux?.row?.cte);
		setDataHoraEmbarcado(formattedDate);
		setStatus(
			props.listaStatus.find((item) => item.nome === tabelaRedux?.row?.status)
		);
		setValuesSelect((current) => ({
			...current,
			status: {
				title: tabelaRedux?.row?.status,
				obj: tabelaRedux?.row?.idStatus,
			},
		}));
	}, [tabelaRedux?.row?.cte, formattedDate, props.listaStatus]);

	const clearStatus = () => {
		setStatus(null);
		setErrorStatus(false);
	};

	const handleCloseCadastroStatus = () => {
		if (idAtual) {
			setOpenCadastroStatus(false);
			setDadosRequestControllerRedux("Close Status");
		} else {
			setDadosRequestControllerRedux("Close Status");
			setOpenCadastroStatus(false);
		}
	};

	const isLoading = useMemo(() => {
		return lancandoCTE || !profile?.token;
	}, [lancandoCTE, profile?.token]);

	return (
		<div>
			<Modal isOpen={open} onClose={handleClose}>
				<div
					className="modal-lancerCte-container"
					onClick={(e) => e.stopPropagation()}
				>
					<DialogTitle id="alert-dialog-title">{props.titulo}</DialogTitle>
					<div className="modal-lancerCte-select-status">
						<SelectInputField
							label="Status:"
							options={statusOptions}
							onChange={(e) => {
								setStatus(e.obj);
								setErrorStatus(false);
								setValuesSelect((current) => ({
									...current,
									status: {
										title: e.title,
										obj: e.obj,
									},
								}));
							}}
							onClear={clearStatus}
							value={valuesSelect.status.title || ""}
							onInputChange={handleStatusInputChange}
							error={errorStatus}
						/>
						<Separator size="sm" direction="horizontal" />
						<NewIcon
							onClick={() => {
								setOpenCadastroStatus(true);
								setIdAtual(0);
								setDisable(false);
							}}
						/>
					</div>
					<Separator size="md" />
					<InputField
						type="text"
						label="CTE:"
						value={cte || ""}
						onChange={(e) => {
							setCTE(e.target.value.replace(/\D/g, ""));
							setErrorCte(false);
						}}
						error={!!errorCte}
					/>
					<Separator size="md" />
					<InputField
						type="datetime-local"
						label="Data hora:"
						value={dataHoraEmbarcado || ""}
						onChange={(e) => {
							setDataHoraEmbarcado(formatDateHora(new Date(e.target.value)));
							setErrorDataHoraEmbarcado(false);
						}}
						error={!!errorDataHoraEmbarcado}
					/>
					<Separator size="md" />
					<DialogActions>
						<CustomButtonV2
							classCss="custom-button-cadastrar"
							classText="button-texto-cadastrar"
							titulo="OK"
							cor="#0946B8"
							onClick={handleDefinirCteStatus}
						/>
						<CustomButtonV2
							classCss="custom-button-cancelar"
							classText="button-texto-cancelar"
							titulo="Cancelar"
							cor="#0946B8"
							onClick={handleClose}
						/>
					</DialogActions>
				</div>
			</Modal>
			{isLoading && (
				<div className="loading">
					<LoadingComponent />
				</div>
			)}
			<ModalCadastro
				titulo="Cadastro"
				tituloConfirmar="OK"
				cadastroStatus="sim"
				open={openCadastroStatus}
				idAtual={idAtual}
				desabilitar={disable}
				onClose={handleCloseCadastroStatus}
			/>
		</div>
	);
}

export default ModalLancarCTEStatus;
